/*
 * © 2026 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ServerError = exports.MaintenanceWindowError = exports.RequirementsNotMetError = exports.ServiceUnavailableError = exports.BadGatewayError = exports.TagsForOrganizationWithoutGroupError = exports.TestLimitReachedError = exports.UnauthorisedError = exports.TimeoutError = exports.BadRequestError = exports.NotImplementedError = exports.TooManyRequestsError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name TooManyRequestsError
 * @description The request rate limit has been exceeded. Wait a few minutes, then try again.
 * @summary Service temporarily throttled
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TooManyRequestsError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Service temporarily throttled',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0001',
            status: 429,
            description: `The request rate limit has been exceeded. Wait a few minutes, then try again.`,
            errorCode: 'SNYK-0001',
            level: 'warn',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TooManyRequestsError = TooManyRequestsError;
/**
 * @class
 * @name NotImplementedError
 * @description The server doesn't recognize the request method, or it cannot fulfill it. Review the request and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-api-info](https://docs.snyk.io/snyk-api-info)
 * @summary Server error response
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NotImplementedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Server error response',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0002',
            status: 501,
            description: `The server doesn't recognize the request method, or it cannot fulfill it. Review the request and try again.`,
            errorCode: 'SNYK-0002',
            level: 'error',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-api-info',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NotImplementedError = NotImplementedError;
/**
 * @class
 * @name BadRequestError
 * @description The server cannot process the request due to a client error, such as malformed request syntax, size too large, invalid request message framing, or deceptive request routing. Review the request and try again.
 * @summary Client request cannot be processed
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class BadRequestError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Client request cannot be processed',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0003',
            status: 400,
            description: `The server cannot process the request due to a client error, such as malformed request syntax, size too large, invalid request message framing, or deceptive request routing. Review the request and try again.`,
            errorCode: 'SNYK-0003',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.BadRequestError = BadRequestError;
/**
 * @class
 * @name TimeoutError
 * @description The server timed out during the request. Check Snyk status, then try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * @summary Server communication error
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TimeoutError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Server communication error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0004',
            status: 504,
            description: `The server timed out during the request. Check Snyk status, then try again.`,
            errorCode: 'SNYK-0004',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TimeoutError = TimeoutError;
/**
 * @class
 * @name UnauthorisedError
 * @description Authentication credentials not recognized, or user access is not provisioned. Revise credentials and try again, or request access from your Snyk administrator.
 * @summary Authentication error
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UnauthorisedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Authentication error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0005',
            status: 401,
            description: `Authentication credentials not recognized, or user access is not provisioned. Revise credentials and try again, or request access from your Snyk administrator.`,
            errorCode: 'SNYK-0005',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UnauthorisedError = UnauthorisedError;
/**
 * @class
 * @name TestLimitReachedError
 * @description You have reached the maximum number of tests in your Snyk plan. This causes Snyk tests on PRs and CLI to fail. Deactivate Snyk Test on your Project or upgrade your Snyk plan.
 *
 * See more:
 * - [https://support.snyk.io/s/article/Rate-limit-hit-while-testing-the-project](https://support.snyk.io/s/article/Rate-limit-hit-while-testing-the-project)
 * - [https://docs.snyk.io/scan-using-snyk/working-with-snyk-in-your-environment/what-counts-as-a-test](https://docs.snyk.io/scan-using-snyk/working-with-snyk-in-your-environment/what-counts-as-a-test)
 * - [https://support.snyk.io/s/article/Snyk-Test-of-PR-failing-due-to-test-limit](https://support.snyk.io/s/article/Snyk-Test-of-PR-failing-due-to-test-limit)
 * @summary Test limit reached
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TestLimitReachedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Test limit reached',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0006',
            status: 429,
            description: `You have reached the maximum number of tests in your Snyk plan. This causes Snyk tests on PRs and CLI to fail. Deactivate Snyk Test on your Project or upgrade your Snyk plan.`,
            errorCode: 'SNYK-0006',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://support.snyk.io/s/article/Rate-limit-hit-while-testing-the-project',
                'https://docs.snyk.io/scan-using-snyk/working-with-snyk-in-your-environment/what-counts-as-a-test',
                'https://support.snyk.io/s/article/Snyk-Test-of-PR-failing-due-to-test-limit',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TestLimitReachedError = TestLimitReachedError;
/**
 * @class
 * @name TagsForOrganizationWithoutGroupError
 * @description This error occures when trying to add tags to Organizations that that are part of a Group.
 *
 * Verify with your Group Admin if the Organization should be in a Group.
 *
 * If you have more than one Organization, you can set the Organization with which new Projects should be associated by running `snyk config set org=ORG_ID`.
 *
 * If you want to override this global configuration for individual runs of snyk monitor, `run snyk test --org=ORG_ID` or `snyk monitor --org=ORG_ID`.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-admin/snyk-projects/project-tags](https://docs.snyk.io/snyk-admin/snyk-projects/project-tags)
 * @summary Organization is not part of a group
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TagsForOrganizationWithoutGroupError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Organization is not part of a group',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0007',
            status: 422,
            description: `This error occures when trying to add tags to Organizations that that are part of a Group.

Verify with your Group Admin if the Organization should be in a Group.

If you have more than one Organization, you can set the Organization with which new Projects should be associated by running \`snyk config set org=ORG_ID\`.

If you want to override this global configuration for individual runs of snyk monitor, \`run snyk test --org=ORG_ID\` or \`snyk monitor --org=ORG_ID\`.`,
            errorCode: 'SNYK-0007',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-admin/snyk-projects/project-tags',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TagsForOrganizationWithoutGroupError = TagsForOrganizationWithoutGroupError;
/**
 * @class
 * @name BadGatewayError
 * @description Due to a server gateway error, the server cannot process the request. Check Snyk status and try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * @summary Unable to fulfill the request
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class BadGatewayError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to fulfill the request',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0008',
            status: 502,
            description: `Due to a server gateway error, the server cannot process the request. Check Snyk status and try again.`,
            errorCode: 'SNYK-0008',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.BadGatewayError = BadGatewayError;
/**
 * @class
 * @name ServiceUnavailableError
 * @description Due to service availability issues, Snyk cannot process the request. This issue is unexpected, and the service will recover shortly. If the error still occurs, contact Snyk Support.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * @summary Unable to fulfill the request
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ServiceUnavailableError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to fulfill the request',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0009',
            status: 503,
            description: `Due to service availability issues, Snyk cannot process the request. This issue is unexpected, and the service will recover shortly. If the error still occurs, contact Snyk Support.`,
            errorCode: 'SNYK-0009',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ServiceUnavailableError = ServiceUnavailableError;
/**
 * @class
 * @name RequirementsNotMetError
 * @description The environment is missing requirements to execute the application successfully.
 * @summary Missing runtime requirements
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class RequirementsNotMetError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Missing runtime requirements',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0010',
            status: 0,
            description: `The environment is missing requirements to execute the application successfully.`,
            errorCode: 'SNYK-0010',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.RequirementsNotMetError = RequirementsNotMetError;
/**
 * @class
 * @name MaintenanceWindowError
 * @description We are currently unavailable due to a maintenance window. For additional information please visit our status pages. Thank you for your patience.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * @summary Unavailable due to maintenance
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class MaintenanceWindowError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unavailable due to maintenance',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-0099',
            status: 503,
            description: `We are currently unavailable due to a maintenance window. For additional information please visit our status pages. Thank you for your patience.`,
            errorCode: 'SNYK-0099',
            level: 'error',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.MaintenanceWindowError = MaintenanceWindowError;
/**
 * @class
 * @name ServerError
 * @description The server cannot process the request due to an unexpected error. Check Snyk status, then try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * @summary Unable to process request
 * @category Snyk
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ServerError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to process request',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-9999',
            status: 500,
            description: `The server cannot process the request due to an unexpected error. Check Snyk status, then try again.`,
            errorCode: 'SNYK-9999',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ServerError = ServerError;
//# sourceMappingURL=Snyk-error-catalog.js.map